import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { useLocale } from '@app/core/hooks/useLocale';
import { FC, useMemo } from 'react';
import CellValue from '@app/shared/components/cell-value/CellValue';
import styles from '../../opm-inputs.module.scss';
import {
  TABLE_COL_WIDTH,
  TABLE_LABELS_COL_WIDTH_OPM_INPUTS,
} from '@app/shared/constants/table-config';
import {
  amountWithDecimalRules,
  shortDateViewRules,
  textViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import GridTextField from '@app/shared/components/grid-controls/grid-text-field/GridTextField';
import { amountBasicFieldFormattingProps } from '@app/shared/components/form-controls/form-field/form-field-patterns';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { numberPattern } from '@app/shared/components/grid-controls/grid-text-field/grid-cell-value-patterns';
import { OpmSimulatedValue } from '@app/shared/models/contracts/enums/shared-enums';
import { enumKeyByValue } from '@app/shared/helpers';

const CapitalStructureForEvents: FC = (): JSX.Element => {
  const opmInputResults = useAppSelector((state) => state.opmCalculation.calculatedInputs);
  const valuationDateRaw = useAppSelector((state) => state.project.projectDraft.valuationDate);
  const opmSimulatedValue = useAppSelector(
    (state) => state.project.projectDraft.opmInput.opmSimulatedValue
  );
  const { l } = useLocale();

  const isEquitySimulation =
    opmSimulatedValue === enumKeyByValue(OpmSimulatedValue, OpmSimulatedValue.Equity);

  const events = useMemo(() => {
    const valuationDate = Date.parse(valuationDateRaw);
    return Object.fromEntries(
      Object.entries(opmInputResults?.events ?? {}).filter(([_, eventDetails]) => {
        const eventDate = Date.parse(eventDetails.date);
        return eventDetails.narrative !== 'Original Structure' && eventDate > valuationDate;
      })
    );
  }, [opmInputResults?.events, valuationDateRaw]);

  const hasInfusions = useMemo(
    () =>
      Object.values(events).some(
        (event) => event.addition !== null && event.addition !== undefined && event.addition !== 0
      ),
    [events]
  );

  const hasRedemptions = useMemo(
    () =>
      Object.values(events).some(
        (event) =>
          event.redemption !== null && event.redemption !== undefined && event.redemption !== 0
      ),
    [events]
  );

  if (!events || Object.keys(events).length === 0) {
    return <></>;
  }

  return (
    <div>
      <table
        style={{ marginBottom: '1.6rem' }}
        className={`table-primary table-primary--zebra ${styles[`opm-table-header`]}`}>
        <colgroup>
          <col style={{ minWidth: `${TABLE_LABELS_COL_WIDTH_OPM_INPUTS}px` }} />
          <col style={{ minWidth: `${TABLE_COL_WIDTH}px` }} />
          {Object.keys(events).map((eventId) => (
            <col key={eventId} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className={`table-primary__cell--header-tertiary ${styles[`table-title`]}`}>
              {l('_EquityRefinancingInputs')}
            </th>
            {Object.entries(events).map(([eventId, event]) => (
              <th key={eventId} className="table-primary__cell--header-tertiary">
                <CellValue value={event.narrative} {...textViewRules} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th className={`table-primary__cell--vertical-separator ${styles[`opm-table-rows`]}`}>
              {l('_EventDate')}
            </th>
            {Object.entries(events).map(([eventId, event]) => (
              <td key={`${eventId}-eventDate`}>
                <CellValue value={event.date} normalize {...shortDateViewRules} />
              </td>
            ))}
          </tr>
          {hasInfusions && (
            <tr>
              <th className={`table-primary__cell--vertical-separator ${styles[`opm-table-rows`]}`}>
                {l('_EquityInfusion')}
              </th>
              {Object.keys(events).map((eventId) => (
                <td key={`${eventId}-addition`}>
                  <CellValue
                    value={opmInputResults.events[eventId]?.addition}
                    normalize
                    {...amountWithDecimalRules}
                  />
                </td>
              ))}
            </tr>
          )}
          {hasRedemptions && (
            <tr>
              <th className={`table-primary__cell--vertical-separator ${styles[`opm-table-rows`]}`}>
                {l('_EquityRedemption')}
              </th>
              {Object.keys(events).map((eventId) => (
                <td key={`${eventId}-redemption`}>
                  <CellValue
                    value={opmInputResults.events[eventId]?.redemption}
                    normalize
                    {...amountWithDecimalRules}
                  />
                </td>
              ))}
            </tr>
          )}
          {!isEquitySimulation && (
            <tr>
              <th className={`table-primary__cell--vertical-separator ${styles[`opm-table-rows`]}`}>
                {l('_IncrementalNetDebt')}
              </th>
              {Object.keys(events).map((eventId, index) => (
                <td key={`${eventId}-netDebt`}>
                  <GridTextField
                    name={`netDebtPerEvent.${eventId}`}
                    data-testid={`netDebtPerEvent-${index}`}
                    {...amountBasicFieldFormattingProps}
                    fieldValueMask={numberPattern}
                    alignment={Alignment.Right}
                    placeholder="0"
                  />
                </td>
              ))}
            </tr>
          )}
          <tr>
            <th className={`table-primary__cell--vertical-separator ${styles[`opm-table-rows`]}`}>
              <strong>{l('_NetChangeInCapital')}</strong>
            </th>
            {Object.keys(events).map((eventId) => (
              <td key={`${eventId}-netChangeInCapital`}>
                <CellValue
                  value={opmInputResults.events[eventId]?.netChangeInCapital}
                  normalize
                  {...amountWithDecimalRules}
                  strong
                />
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default CapitalStructureForEvents;
